import { Link } from "react-router-dom";
import { Heart, ArrowRight, HandHeart, Leaf, BookOpen, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Reveal, Overline } from "@/components/Reveal";
import { STATS, NEWS, IMAGES, SHOW_NEWS } from "@/data/site";
import { useLang } from "@/i18n/LanguageContext";

const PILLAR_ICONS = [Leaf, BookOpen, Users];

export default function Home() {
  const { t, lang } = useLang();
  const h = t.home;
  const heroImg = lang === "es" ? IMAGES.heroEs : IMAGES.heroEn;

  return (
    <>
      {/* Hero */}
      <section className="relative min-h-[92vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={heroImg} alt={lang === "es" ? "Voluntaria sosteniendo un cartel que dice Esperanza" : "Volunteer holding a sign that reads Hope"} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0A2540]/72" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 w-full">
          <div className="max-w-2xl">
            <Reveal>
              <Overline className="text-[#4ade80]">{h.heroOverline}</Overline>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="mt-5 font-display font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                {h.heroPre}<span className="text-[#4ade80]">{h.heroAccent}</span>{h.heroPost}
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-lg sm:text-xl text-white/85 leading-relaxed max-w-xl">{h.heroSub}</p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-9 flex flex-col sm:flex-row gap-4">
                <Button asChild size="lg" className="bg-[#E85D04] hover:bg-[#CC5202] text-white font-semibold rounded-full h-14 px-8 text-base shadow-lg hover:shadow-xl transition-all">
                  <Link to="/donate" data-testid="hero-donate-button">
                    <Heart className="w-5 h-5 mr-2" aria-hidden="true" /> {t.donateNow}
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="bg-white/10 border-white/40 text-white hover:bg-white hover:text-[#0A2540] font-semibold rounded-full h-14 px-8 text-base backdrop-blur-sm transition-all">
                  <Link to="/get-involved" data-testid="hero-volunteer-button">
                    <HandHeart className="w-5 h-5 mr-2" aria-hidden="true" /> {t.volunteer}
                  </Link>
                </Button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Impact stats */}
      <section className="bg-[#0A2540]" data-testid="stats-section" aria-label={h.statsAria}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((s, i) => (
            <Reveal key={i} delay={i * 0.08} className="text-center lg:text-left">
              <div className="font-display font-extrabold text-3xl sm:text-4xl text-[#4ade80]">{s.value}</div>
              <div className="mt-2 text-sm text-white/70 font-medium">{h.stats[i]}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Mission intro */}
      <section className="py-20 sm:py-28" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <Overline>{h.missionOverline}</Overline>
            <h2 className="mt-4 font-display font-bold text-3xl sm:text-4xl text-[#0A2540] tracking-tight leading-tight">{h.missionTitle}</h2>
            <p className="mt-6 text-lg text-[#475569] leading-relaxed">{t.mission}</p>
            <Button asChild variant="ghost" className="mt-6 text-[#16A34A] hover:text-[#0A2540] font-semibold px-0 hover:bg-transparent">
              <Link to="/about" data-testid="learn-more-link">
                {h.learnStory} <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <img src={IMAGES.sorting} alt="Volunteers sorting food donations at the pantry" className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]" />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-lg p-5 border border-[#E2E8F0] hidden sm:block">
                <div className="font-display font-extrabold text-2xl text-[#E85D04]">{h.since}</div>
                <div className="text-sm text-[#475569]">{h.sinceSub}</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-20 sm:py-28 bg-[#F1F5F9]" data-testid="pillars-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <Overline>{h.pillarsOverline}</Overline>
            <h2 className="mt-4 font-display font-bold text-3xl sm:text-4xl text-[#0A2540] tracking-tight">{h.pillarsTitle}</h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-8">
            {h.pillars.map((p, i) => {
              const Icon = PILLAR_ICONS[i];
              return (
                <Reveal key={i} delay={i * 0.1}>
                  <Card className="h-full bg-white border-[#E2E8F0] rounded-2xl p-8 shadow-sm hover:-translate-y-1 hover:shadow-md transition-all">
                    <div className="w-14 h-14 rounded-xl bg-[#16A34A]/10 flex items-center justify-center">
                      <Icon className="w-7 h-7 text-[#16A34A]" aria-hidden="true" />
                    </div>
                    <h3 className="mt-5 font-display font-semibold text-xl text-[#0A2540]">{p.title}</h3>
                    <p className="mt-3 text-[#475569] leading-relaxed">{p.text}</p>
                  </Card>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Recent news */}
      {SHOW_NEWS && (
      <section className="py-20 sm:py-28" data-testid="news-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <Overline>{h.newsOverline}</Overline>
            <h2 className="mt-4 font-display font-bold text-3xl sm:text-4xl text-[#0A2540] tracking-tight">{h.newsTitle}</h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-8">
            {h.news.map((n, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <Card className="group h-full overflow-hidden bg-white border-[#E2E8F0] rounded-2xl shadow-sm hover:shadow-md transition-all">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={NEWS[i].image} alt={n.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 text-xs font-semibold">
                      <span className="text-[#E85D04] uppercase tracking-wider">{n.tag}</span>
                      <span className="text-[#94a3b8]">{n.date}</span>
                    </div>
                    <h3 className="mt-3 font-display font-semibold text-lg text-[#0A2540] leading-snug">{n.title}</h3>
                    <p className="mt-2 text-sm text-[#475569] leading-relaxed">{n.excerpt}</p>
                  </div>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* CTA band */}
      <section className="py-20" data-testid="cta-band">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-[#16A34A] px-8 py-16 sm:px-16 text-center">
            <div className="relative max-w-2xl mx-auto">
              <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-white tracking-tight">{h.ctaTitle}</h2>
              <p className="mt-4 text-white/90 text-lg">{h.ctaBody}</p>
              <Button asChild size="lg" className="mt-8 bg-white text-[#16A34A] hover:bg-[#0A2540] hover:text-white font-bold rounded-full h-14 px-10 text-base transition-all">
                <Link to="/donate" data-testid="cta-donate-button">
                  <Heart className="w-5 h-5 mr-2" /> {h.ctaButton}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
